/**
 * O recorte do Faturômetro vive na URL.
 *
 * Antes daqui tudo morava no `data()` da view. Três consequências, e as três
 * apareceram em uso real: o link não era compartilhável (mandar "olha a
 * estrutura do Wesley em agosto" exigia instruções, não um link), o botão
 * voltar do navegador saía da tela em vez de desfazer o filtro, e F5 zerava o
 * recorte inteiro.
 *
 * REGRA DE OURO DESTE ARQUIVO: só entra na URL o que DIFERE do padrão. Uma tela
 * recém-aberta tem `?` vazio. Sem isso a barra de endereço vira uma parede de
 * `false` e `null` que ninguém consegue ler nem editar à mão, e um link que
 * "parece" filtrado sem estar.
 *
 * O catálogo de colunas NÃO entra: ele é preferência de quem olha, não recorte
 * do que se olha, e já vive em localStorage por usuário. Mandar a tela de
 * alguém com as colunas de outro é o oposto do que se quer ao compartilhar.
 *
 * Módulo puro (CommonJS) porque os testes rodam em node puro, sem bundler —
 * mesma escolha de kpiColumns/kpiTree/kpiFilters.
 */

/** Padrões. Um campo igual ao padrão não é serializado. */
const PADROES = {
  scope: 'network',
  perspective: 'owner',
  contractStatus: 'valid',
  snapshotMode: 'live',
  showOutline: false,
  allConsultants: false,
  includeInactiveFranchises: false,
  includeInactiveNetworks: false,
  disableContractNetworks: false,
  includeEmployees: false,
}

/** Nome curto na URL -> campo do estado. Curto porque é para ler e editar. */
const TEXTO = {
  recorte: 'scope', atribuicao: 'perspective', status: 'contractStatus', modo: 'snapshotMode',
}
const BOOL = {
  numeracao: 'showOutline',
  todos: 'allConsultants',
  franqInativas: 'includeInactiveFranchises',
  redesInativas: 'includeInactiveNetworks',
  semRedeContrato: 'disableContractNetworks',
  colaboradores: 'includeEmployees',
}
/**
 * Filtro de produto. Vive no topo do estado, e não em `criteria`, porque é
 * filtro de SERVIDOR: muda o que a consulta soma, não o que a tela esconde.
 */
const LISTA_TOPO = { solucao: 'financialSolutionIds', empresa: 'partnerCompanyIds' }

/** Critérios do painel "Recorte". */
const LISTA = { franquia: 'franchiseIds', unidade: 'physicalFranchiseIds', cargo: 'roleIds' }
const NUMERO = { nivel: 'maxDepth', min: 'minTotal' }

const listaParaTexto = valores => (valores || []).map(v => String(v)).join(',')

const textoParaLista = texto => String(texto || '')
  .split(',')
  .map(pedaco => pedaco.trim())
  .filter(Boolean)
  .map(Number)
  .filter(n => Number.isFinite(n))

/**
 * Estado -> query. Devolve objeto simples, pronto para `$router.replace`.
 *
 * `periodo` sai como `inicio~fim` em vez de dois parâmetros: é um valor só
 * conceitualmente, e separar em dois abre a porta para URL com início sem fim.
 */
const toQuery = (estado = {}) => {
  const query = {}
  const criteria = estado.criteria || {}

  if (estado.startDate && estado.endDate) {
    query.periodo = `${estado.startDate}~${estado.endDate}`
  }

  Object.keys(TEXTO).forEach(chave => {
    const campo = TEXTO[chave]
    const valor = estado[campo]
    if (valor !== undefined && valor !== null && valor !== PADROES[campo]) {
      query[chave] = String(valor)
    }
  })

  Object.keys(BOOL).forEach(chave => {
    const campo = BOOL[chave]
    if (estado[campo] === true && PADROES[campo] !== true) {
      query[chave] = '1'
    }
  })

  if (criteria.name) {
    query.nome = String(criteria.name)
  }

  Object.keys(LISTA).forEach(chave => {
    const valores = criteria[LISTA[chave]]
    if (valores && valores.length) {
      query[chave] = listaParaTexto(valores)
    }
  })

  Object.keys(NUMERO).forEach(chave => {
    const valor = criteria[NUMERO[chave]]
    if (valor !== null && valor !== undefined && valor !== '') {
      query[chave] = String(valor)
    }
  })

  if (criteria.onlyWithMovement === true) {
    query.mov = '1'
  }

  Object.keys(LISTA_TOPO).forEach(chave => {
    const valores = estado[LISTA_TOPO[chave]]
    if (valores && valores.length) {
      query[chave] = listaParaTexto(valores)
    }
  })

  const ids = (estado.consultantIds || []).filter(id => id !== null && id !== undefined)
  if (ids.length) {
    query.pn = listaParaTexto(ids)
  }

  return query
}

/**
 * Query -> estado. Só devolve o que a URL trouxe; quem chama mescla no padrão.
 *
 * Nada aqui confia no que veio: a URL é digitável. Valor de enum fora do
 * catálogo é descartado em vez de virar estado inválido — caso contrário
 * `?recorte=banana` chegaria na query do backend.
 */
const fromQuery = (query = {}) => {
  const estado = {}
  const criteria = {}

  const periodo = String(query.periodo || '')
  const partes = periodo.split('~')
  const eData = valor => /^\d{4}-\d{2}-\d{2}$/.test(valor)
  if (partes.length === 2 && eData(partes[0]) && eData(partes[1])) {
    const [inicio, fim] = partes
    estado.startDate = inicio <= fim ? inicio : fim
    estado.endDate = inicio <= fim ? fim : inicio
  }

  const ENUMS = {
    scope: ['own', 'direct', 'network'],
    perspective: ['owner', 'specialist'],
    contractStatus: ['valid', 'gross'],
    snapshotMode: ['live', 'snapshot', 'auto'],
  }

  Object.keys(TEXTO).forEach(chave => {
    if (query[chave] === undefined) {
      return
    }
    const campo = TEXTO[chave]
    const valor = String(query[chave])
    if (ENUMS[campo].indexOf(valor) >= 0) {
      estado[campo] = valor
    }
  })

  Object.keys(BOOL).forEach(chave => {
    if (query[chave] !== undefined) {
      estado[BOOL[chave]] = String(query[chave]) === '1'
    }
  })

  // `!== undefined` nao basta. O vue-router entrega `?nome` (sem `=`) como
  // null, e `String(null)` e a string "null" -- que entra no v-model e aparece
  // escrita dentro do campo, como se alguem tivesse digitado. O mesmo vale para
  // os numericos logo abaixo, onde `Number(null)` e 0: um `?min` vazio viraria
  // o filtro "faturamento minimo = 0", que nao e o mesmo que filtro nenhum.
  if (query.nome !== undefined && query.nome !== null) {
    criteria.name = String(query.nome)
  }

  Object.keys(LISTA).forEach(chave => {
    if (query[chave] !== undefined) {
      criteria[LISTA[chave]] = textoParaLista(query[chave])
    }
  })

  Object.keys(NUMERO).forEach(chave => {
    if (query[chave] === undefined || query[chave] === null || query[chave] === '') {
      return
    }
    const numero = Number(query[chave])
    if (Number.isFinite(numero)) {
      criteria[NUMERO[chave]] = numero
    }
  })

  if (query.mov !== undefined) {
    criteria.onlyWithMovement = String(query.mov) === '1'
  }

  Object.keys(LISTA_TOPO).forEach(chave => {
    if (query[chave] !== undefined) {
      estado[LISTA_TOPO[chave]] = textoParaLista(query[chave])
    }
  })

  if (query.pn !== undefined) {
    estado.consultantIds = textoParaLista(query.pn)
  }

  if (Object.keys(criteria).length) {
    estado.criteria = criteria
  }

  return estado
}

/** Duas querys são o mesmo recorte? Evita `replace` redundante no router. */
const mesmaQuery = (a = {}, b = {}) => {
  const chaves = Object.keys(a).concat(Object.keys(b))
  return chaves.every(chave => String(a[chave] === undefined ? '' : a[chave])
    === String(b[chave] === undefined ? '' : b[chave]))
}

module.exports = {
  PADROES,
  toQuery,
  fromQuery,
  mesmaQuery,
}
